import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { billingKeys } from '@/lib/queryKeys'
import { addBillingCredit } from '../api/api'
import type { BillingCreditRequest } from '../types'

/** Previews (`dry_run`) or writes a credit for the organization on screen.
 *  Only a write refetches the billing page, including one that failed, since
 *  it may have landed; a preview changes nothing. */
export function useAddBillingCreditMutation() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: Omit<BillingCreditRequest, 'org_slug'>) => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      if (!organization?.slug) throw new Error('No organization selected')
      return addBillingCredit(token, { ...body, org_slug: organization.slug })
    },
    onSettled: (_result, _error, body) => {
      if (body.dry_run || !organization) return
      return queryClient.invalidateQueries({
        queryKey: billingKeys.all(organization.id),
      })
    },
  })
}
